/**
 * Duplicate detection hook for collection imports
 *
 * Detects cards that already exist in the collection based on matching strategy:
 * - source_id: Match by source_id (most accurate for re-imports)
 * - fingerprint: Match by set + collector_number + finish + condition + folder
 *   (uses scryfall_id lookup when available, falls back to raw set+collector)
 * - none: No reliable matching available
 *
 * Uses in-memory collection data from Jotai atoms - no Firestore queries needed.
 */
import { useEffect, useMemo } from 'react';

import { useAtomValue } from 'jotai';

import { collectionStateAtom } from '@/features/collection/state/collectionAtom';
import { foldersStateAtom } from '@/features/folders/state/foldersAtom';
import type { MatchingStrategy } from '@/lib/universalCSVParser';
import { cardIndexByIdAtom } from '@/services/cardDatabase';
import type { CollectionCard, Condition, Finish } from '@/types/card';
import type { DuplicateStatus, MatchedImportCard } from '../types';

// ============================================================================
// Types
// ============================================================================

export type { DuplicateStatus } from '../types';

export interface DuplicateDetectionResult {
  /** Cards with duplicate status marked */
  cards: MatchedImportCardWithDuplicate[];
  /** Number of new cards (not in collection) */
  newCount: number;
  /** Number of duplicate cards (already in collection) */
  duplicateCount: number;
  /** Map of import card keys to matched collection card IDs */
  matchedCollectionCards: Map<string, CollectionCard>;
}

export interface MatchedImportCardWithDuplicate extends MatchedImportCard {
  /** Duplicate detection status */
  duplicateStatus: DuplicateStatus;
  /** ID of matched collection card (for update mode) */
  matchedCollectionCardId?: string;
  /** Reference to matched collection card data */
  matchedCollectionCard?: CollectionCard;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Create a unique key for fingerprint matching (set + collector_number + finish + condition + folder_id)
 * Using set + collector_number instead of scryfall_id allows matching even when scryfall lookup fails.
 * Adding folder ensures cards in different folders are treated as distinct.
 */
function createFingerprintKey(
  setCode: string,
  collectorNumber: string,
  finish: Finish,
  condition: Condition,
  folderId: string | null,
): string {
  return `${setCode.toLowerCase()}|${collectorNumber}|${finish}|${condition}|${folderId ?? 'root'}`;
}

// ============================================================================
// Hook
// ============================================================================

export interface UseDuplicateDetectionReturn {
  /** Detect duplicates in the given cards (synchronous - uses in-memory data) */
  detectDuplicates: (cards: MatchedImportCard[], strategy: MatchingStrategy) => DuplicateDetectionResult;
  /** Whether collection data is still loading */
  isLoading: boolean;
}

/**
 * Hook for detecting duplicate cards in an import.
 * Uses in-memory collection data from Jotai atoms for fast lookups.
 */
export function useDuplicateDetection(): UseDuplicateDetectionReturn {
  const collectionState = useAtomValue(collectionStateAtom);
  const foldersState = useAtomValue(foldersStateAtom);
  const cardIndexById = useAtomValue(cardIndexByIdAtom);

  // Build folder name → id lookup
  const folderNameToId = useMemo(() => {
    const folders = foldersState.data ?? [];
    const lookup = new Map<string, string>();
    for (const folder of folders) {
      lookup.set(folder.name.toLowerCase(), folder.id);
    }
    return lookup;
  }, [foldersState.data]);

  // Build lookup maps from collection data (memoized)
  const lookupMaps = useMemo(() => {
    const collection = collectionState.data ?? [];

    // Map by source_id (Phase 4+ imports)
    const bySourceId = new Map<string, CollectionCard>();
    // Map by fingerprint (set + collector_number + finish + condition + folder_id)
    const byFingerprint = new Map<string, CollectionCard>();

    for (const card of collection) {
      // Source ID mapping
      if (card.source_id) {
        bySourceId.set(card.source_id, card);
      }

      // Fingerprint mapping - look up set+collector from card index
      const cardData = cardIndexById.get(card.scryfall_id);
      if (cardData) {
        const fingerprintKey = createFingerprintKey(cardData.set, cardData.collector_number, card.finish, card.condition, card.folder_id);
        byFingerprint.set(fingerprintKey, card);
      }
    }

    return { bySourceId, byFingerprint };
  }, [collectionState.data, cardIndexById]);

  useEffect(() => {
    console.log('Duplicate detection lookup maps built:', lookupMaps, 'sizes', {
      bySourceId: lookupMaps.bySourceId.size,
      byFingerprint: lookupMaps.byFingerprint.size,
    });
  }, [lookupMaps]);

  const detectDuplicates = (cards: MatchedImportCard[], strategy: MatchingStrategy): DuplicateDetectionResult => {
    const resultCards: MatchedImportCardWithDuplicate[] = [];
    const matchedCollectionCards = new Map<string, CollectionCard>();
    let newCount = 0;
    let duplicateCount = 0;

    if (strategy === 'none') {
      console.log('No duplicate detection - all cards treated as new');
      // No reliable matching - treat all as new
      for (const card of cards) {
        resultCards.push({
          ...card,
          duplicateStatus: 'new',
        });
        newCount++;
      }
    } else if (strategy === 'source_id') {
      console.log('Detecting duplicates using source_id strategy');
      // Match by source_id
      for (const card of cards) {
        const existingCard = card.sourceId ? lookupMaps.bySourceId.get(card.sourceId) : undefined;
        console.log('Checking card', card.sourceId, 'found existing card:', existingCard);

        if (existingCard) {
          resultCards.push({
            ...card,
            duplicateStatus: 'duplicate',
            matchedCollectionCardId: existingCard.id,
            matchedCollectionCard: existingCard,
          });
          matchedCollectionCards.set(card.sourceId!, existingCard);
          duplicateCount++;
        } else {
          resultCards.push({
            ...card,
            duplicateStatus: 'new',
          });
          newCount++;
        }
      }
    } else if (strategy === 'fingerprint') {
      console.log('Detecting duplicates using fingerprint strategy');
      // Match by set + collector_number + finish + condition + folder
      for (const card of cards) {
        // Try to get set+collector from matched card, fall back to original import data
        const setCode = card.matched?.set ?? card.setCode;
        const collectorNumber = card.matched?.collector_number ?? card.collectorNumber;

        if (setCode && collectorNumber) {
          // Resolve folder name to folder ID for comparison
          const folderId = card.folderName ? (folderNameToId.get(card.folderName.toLowerCase()) ?? null) : null;
          const fingerprintKey = createFingerprintKey(setCode, collectorNumber, card.finish, card.condition, folderId);
          const existingCard = lookupMaps.byFingerprint.get(fingerprintKey);

          if (existingCard) {
            resultCards.push({
              ...card,
              duplicateStatus: 'duplicate',
              matchedCollectionCardId: existingCard.id,
              matchedCollectionCard: existingCard,
            });
            matchedCollectionCards.set(fingerprintKey, existingCard);
            duplicateCount++;
          } else {
            resultCards.push({
              ...card,
              duplicateStatus: 'new',
            });
            newCount++;
          }
        } else {
          // No set/collector info available - treat as new
          resultCards.push({
            ...card,
            duplicateStatus: 'new',
          });
          newCount++;
        }
      }
    }

    return {
      cards: resultCards,
      newCount,
      duplicateCount,
      matchedCollectionCards,
    };
  };

  return {
    detectDuplicates,
    isLoading: collectionState.isLoading,
  };
}
